import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import { useAuth } from '../contexts/AuthContext';
import { keyframes } from '@mui/system';
import Fade from '@mui/material/Fade';
import StandardCard from './common/StandardCard';
import DashboardHero from './shared/DashboardHero';
import DashboardStatCard from './shared/DashboardStatCard';
import ExpandableCard from './shared/ExpandableCard';
import ViewModeBanner from './shared/ViewModeBanner';
import ClinicSelector from './shared/ClinicSelector';
import StartVideoCallModal from './StartVideoCallModal';
import CalendarView from './shared/CalendarView';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MessageIcon from '@mui/icons-material/Message';
import GroupIcon from '@mui/icons-material/Group';
import NotificationBell from './notifications/NotificationBell';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CopyrightIcon from '@mui/icons-material/Copyright';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
// New icons for missing components
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import ScienceIcon from '@mui/icons-material/Science';
import RadiologyIcon from '@mui/icons-material/MedicalServices';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StoreIcon from '@mui/icons-material/Store';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ClinicDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Real data from API
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [recentMessages] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Real clinic stats from API
  const [clinicStats, setClinicStats] = useState([
    {
      id: 1,
      title: 'Today\'s Patients',
      value: '0',
      status: 'Loading...',
      icon: <GroupIcon />,
      color: 'info'
    },
    {
      id: 2,
      title: 'Messages',
      value: '0',
      status: 'Loading...',
      icon: <MessageIcon />,
      color: 'info'
    }
  ]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Fetch appointment stats
      try {
        const { getAppointmentStats, getUpcomingAppointments } = await import('../services/appointmentsService');

        const [statsResult, appointmentsResult] = await Promise.all([
          getAppointmentStats(),
          getUpcomingAppointments(user?.id, 5)
        ]);

        if (statsResult?.data) {
          setClinicStats(prev => [
            { ...prev[0], value: String(statsResult.data.upcoming || 0), status: statsResult.data.upcoming > 0 ? `${statsResult.data.upcoming} upcoming` : 'No appointments scheduled' },
            prev[1]
          ]);
        }

        if (appointmentsResult?.data) {
          setTodaysAppointments(appointmentsResult.data);
        }
      } catch (err) {
        console.error('Failed to load appointment data:', err);
        setClinicStats(prev => [
          { ...prev[0], status: 'No appointments scheduled' },
          prev[1]
        ]);
      }

      // Fetch unread message count
      try {
        const apiClient = (await import('../services/apiClient')).default;
        const unreadResult = await apiClient.getUnreadCount();
        const count = unreadResult?.data?.count || unreadResult?.count || 0;
        setClinicStats(prev => [
          prev[0],
          { ...prev[1], value: String(count), status: count > 0 ? `${count} unread` : 'No unread messages' }
        ]);
      } catch (err) {
        setClinicStats(prev => [
          prev[0],
          { ...prev[1], status: 'No unread messages' }
        ]);
      }
    };

    fetchDashboardData();
  }, [user?.id]);

  const navigate = useNavigate();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signout fails, navigate to login
      navigate('/login', { replace: true });
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };



  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <ViewModeBanner />
      {/* Header */}
      <AppBar 
        position="static" 
        color="primary" 
        elevation={2}
        sx={{
          borderBottom: 'none',
          bgcolor: 'primary.dark',
          color: 'common.white',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
          <Toolbar disableGutters>
            <Box 
              component="div" 
              sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                animation: `${slideIn} 0.5s ease-out`,
              }}
            >
              <ClinicSelector />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationBell />
              <Tooltip title="Account">
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 180,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem component={Link} to="/physician/profile" onClick={handleClose}>
                  <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem component={Link} to="/physician/settings" onClick={handleClose}>
                  <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                  Settings
                </MenuItem>
                <MenuItem onClick={handleClose}>
                  <DashboardIcon fontSize="small" sx={{ mr: 1 }} />
                  Admin Dashboard
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Hero Section */}
      <DashboardHero
        greeting={`Welcome, Dr. ${profile?.first_name || 'Physician'}!`}
        description="Manage patient care, appointments, and clinical workflows."
        showVideoCallButton={true}
        showScheduleButton={true}
        scheduleButtonDisabled={false}
        userType="physician"
        onStartVideoCall={handleOpenModal}
      />
      
      {/* Main Content */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          mb: 6, 
          flex: 1, 
          px: { xs: 2, sm: 3 } 
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
          {/* Clinic Stats - Moved up for better visibility */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 }, // Reduced padding for mobile
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              animation: `${fadeIn} 0.5s ease-out 0.1s both`,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{
              fontWeight: 700,
              mb: { xs: 3, sm: 3.5, md: 4 },
              textAlign: 'center',
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
              color: 'text.primary',
              letterSpacing: '-0.01em',
            }}>
              Clinic Overview
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', sm: 'center' },
                alignItems: 'stretch',
                gap: { xs: 2, sm: 2.5, md: 3 },
                width: '100%',
                maxWidth: { xs: '100%', md: '700px' },
                mx: 'auto',
              }}
            >
              <Box sx={{ 
                flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, 
                minWidth: { xs: '100%', sm: '280px' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' }
              }}>
                <DashboardStatCard
                  label={clinicStats[0].title}
                  value={clinicStats[0].value}
                  sublabel={clinicStats[0].status}
                  icon={<GroupIcon color="info" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
                  color={clinicStats[0].color}
                />
              </Box>
              <Box sx={{ 
                flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, 
                minWidth: { xs: '100%', sm: '280px' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' }
              }}>
                <DashboardStatCard
                  label={clinicStats[1].title}
                  value={clinicStats[1].value}
                  sublabel={clinicStats[1].status}
                  icon={<MessageIcon color="info" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
                  color={clinicStats[1].color}
                />
              </Box>
            </Box>
          </Paper>

          {/* Today's Appointments and Messages */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 4 }}>
            {/* Today's Appointments */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out 0.2s both`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 3, md: 4 } }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}>
                  Today's Appointments
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCalendarOpen(true)}
                  startIcon={<CalendarMonthIcon />}
                >
                  View Calendar
                </Button>
              </Box>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {todaysAppointments.map((appointment) => (
                  <Paper
                    key={appointment.id}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2, flexShrink: 0 }}>
                        <LocalHospitalIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {appointment.patient}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.time} • {appointment.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.reason}
                        </Typography>
                        <Typography variant="body2" color="primary.main" sx={{ mt: 1 }}>
                          {appointment.status}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>

            {/* Calendar Modal */}
            <Dialog
              open={calendarOpen}
              onClose={() => setCalendarOpen(false)}
              maxWidth="lg"
              fullWidth
              fullScreen={window.innerWidth < 600}
              PaperProps={{
                sx: {
                  height: { xs: '100vh', sm: '90vh' },
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  m: { xs: 0, sm: 2 }
                }
              }}
            >
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Appointment Calendar</Typography>
                  <IconButton onClick={() => setCalendarOpen(false)} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 0, pt: 1, overflow: 'hidden' }}>
                <CalendarView 
                  height={window.innerHeight * 0.75}
                  showToolbar={true}
                  onSelectSlot={({ start }) => {
                    setCalendarOpen(false);
                    navigate(`/appointments?date=${start.toISOString()}`);
                  }}
                  // onSelectEvent removed - CalendarView now shows popover with management options
                />
              </DialogContent>
            </Dialog>
            
            {/* Recent Messages */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out 0.3s both`,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{
                fontWeight: 600,
                mb: { xs: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}>
                Recent Messages
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {recentMessages.map((message) => (
                  <Paper
                    key={message.id}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>
                      <Avatar sx={{ bgcolor: 'secondary.light', mr: 2, flexShrink: 0 }}>
                        <MessageIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {message.from}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {message.date}
                        </Typography>
                        <Typography variant="body2">
                          {message.preview}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Clinical Tools */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Clinical Tools
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 3 },
                mb: 2,
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(20% - 12px)' }, minWidth: 0 }}>
                <StandardCard 
                  icon={VideocamIcon} 
                  title="Video Consultations" 
                  link="/video-call?userType=physician" 
                  color="primary"
                  buttonText="Start Video Call with Patient"
                  disabled={false}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(20% - 12px)' }, minWidth: 0 }}>
                <StandardCard 
                  icon={GroupIcon} 
                  title="Patient Management" 
                  link="/clinic/patients" 
                  color="secondary"
                  buttonText="Manage Patients"
                  disabled={false}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(20% - 12px)' }, minWidth: 0 }}>
                <StandardCard 
                  icon={CalendarMonthIcon} 
                  title="Schedule" 
                  link="/appointments?userType=physician" 
                  color="info"
                  buttonText="View Schedule"
                  disabled={false}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(20% - 12px)' }, minWidth: 0 }}>
                <StandardCard 
                  icon={MessageIcon} 
                  title="Messages" 
                  link="/messages?userType=physician" 
                  color="success"
                  buttonText="View Messages"
                  disabled={false}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(20% - 12px)' }, minWidth: 0 }}>
                <StandardCard 
                  icon={NoteAltIcon} 
                  title="Clinical Notes" 
                  link="/notes?userType=physician" 
                  color="warning"
                  buttonText="Add Clinical Notes"
                  disabled={false}
                />
              </Box>
            </Box>
          </Paper>

          {/* Patient History & Records - Updated to list format */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Patient History & Records
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Patient Encounters Item */}
              <ExpandableCard
                title="Patient Encounters"
                description="View previous patient visits, treatment history, and clinical notes from past consultations"
                icon={<HistoryIcon />}
                iconColor="primary"
                buttonText="View History"
                buttonColor="primary"
                disabled={true}
              />
              
              {/* Clinical Notes Item */}
              <ExpandableCard
                title="Clinical Notes"
                description="Access and review clinical notes from other physicians, including diagnoses, treatments, and recommendations"
                icon={<NoteAltIcon />}
                iconColor="info"
                buttonText="View Notes"
                buttonColor="info"
                disabled={true}
              />
              
              {/* Media Sharing Item */}
              <ExpandableCard
                title="Media Sharing"
                description="Upload videos, pictures, and medical files to share with patients and other healthcare providers"
                icon={<PhotoCameraIcon />}
                iconColor="secondary"
                buttonText="Coming Soon"
                buttonColor="secondary"
                disabled={true}
              />
            </Box>
          </Paper>

          {/* Connected Services - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Connected Healthcare Services
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, sm: 2.5, md: 3 },
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
                gridTemplateRows: { xs: 'auto auto auto', sm: 'auto auto', md: 'auto auto' },
                gridTemplateAreas: {
                  xs: `
                    'pharmacy lab'
                    'radiology radiology'
                    'transport transport'
                  `,
                  sm: `
                    'pharmacy lab'
                    'radiology transport'
                  `,
                  md: `
                    'pharmacy lab'
                    'radiology transport'
                  `,
                },
                justifyItems: 'stretch',
                alignItems: 'stretch',
                mb: 2,
              }}
            >
              <Box sx={{ gridArea: 'pharmacy', height: '100%' }}>
                <StandardCard 
                  icon={LocalPharmacyIcon} 
                  title="Pharmacy" 
                  description="Send prescriptions & orders"
                  link="#" 
                  color="success"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'lab', height: '100%' }}>
                <StandardCard 
                  icon={ScienceIcon} 
                  title="Laboratory" 
                  description="Order lab tests & view results"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'radiology', height: '100%' }}>
                <StandardCard 
                  icon={RadiologyIcon} 
                  title="Radiology" 
                  description="Request imaging studies"
                  link="#" 
                  color="info"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'transport', height: '100%' }}>
                <StandardCard 
                  icon={DirectionsCarIcon} 
                  title="Transport" 
                  description="Arrange patient transport"
                  link="#" 
                  color="primary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>

          {/* Communication & Integration - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Communication & Integration
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, sm: 2.5, md: 3 },
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
                gridTemplateRows: { xs: 'auto auto', sm: 'auto', md: 'auto' },
                gridTemplateAreas: {
                  xs: `
                    'orders whatsapp'
                    'local local'
                  `,
                  sm: `
                    'orders whatsapp'
                    'local local'
                  `,
                  md: `
                    'orders whatsapp'
                    'local local'
                  `,
                },
                justifyItems: 'stretch',
                alignItems: 'stretch',
                mb: 2,
              }}
            >
              <Box sx={{ gridArea: 'orders', height: '100%' }}>
                <StandardCard 
                  icon={SendIcon} 
                  title="Send Orders" 
                  description="Email orders to services"
                  link="#" 
                  color="primary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ gridArea: 'whatsapp', height: '100%' }}>
                <StandardCard 
                  icon={WhatsAppIcon} 
                  title="WhatsApp Link" 
                  description="Connect via WhatsApp"
                  link="#" 
                  color="success"
                  buttonText="Open WhatsApp"
                  disabled={false}
                  isWhatsApp={true}
                  whatsAppNumber="15551234567"
                  whatsAppMessage="Hello! This is Dr. Demo Physician from Alpha01 Medical Consultants. I need to coordinate patient care."
                />
              </Box>
              <Box sx={{ gridArea: 'local', height: '100%' }}>
                <StandardCard 
                  icon={StoreIcon} 
                  title="Local Services" 
                  description="Connect to stores & restaurants"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: { xs: 5, md: 6 }, 
          bgcolor: 'primary.dark',
          color: 'white',
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'primary.main',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 4, md: 0 },
              mb: 3,
            }}
          >
            <StartVideoCallModal
              open={modalOpen}
              handleClose={handleCloseModal}
              userType="physician"
            />
            {/* Logo, description, social */}
            <Box sx={{ flex: 1, minWidth: 220, mb: { xs: 3, md: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo.svg"
                    alt="Alpha01 Medical Consultants Logo"
                    sx={{
                      height: { xs: 56, md: 40 },
                      mr: 1,
                      display: 'block',
                      borderRadius: 2,
                      padding: 0.5,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.05)' },
                    }}
                  />
                </Box>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  color: 'rgba(255,255,255,0.85)', 
                  fontWeight: 400, 
                  fontSize: { xs: '1.08rem', md: '1.15rem' }, 
                  fontStyle: 'italic',
                  letterSpacing: 0.1,
                  lineHeight: 1.7,
                }}
              >
                Empowering healthcare providers with advanced telemedicine tools.<br />
                Delivering quality patient care through technology.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <TwitterIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <InstagramIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>
            {/* Contact Info */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Contact Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                123 Medical Center Drive
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                San Francisco, CA 94143
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Phone: (555) 123-4567
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Email: providers@alpha01med.com
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)' }}>
              <CopyrightIcon fontSize="small" sx={{ mr: 0.5 }} /> {new Date().getFullYear()} Alpha01 Medical Consultants. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, md: 0 } }}>
              <Typography variant="body2" component={Link} to="/privacy" sx={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 400 }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" component={Link} to="/terms" sx={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 400 }}>
                Terms of Service
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ClinicDashboard;
